export const accessControlStatus = {
  allowed: "Allowed",
  restricted: "Restricted",
  trainingRequired: "Training Required",
  unknown: "Unknown",
};

const MULTI_VALUE_FIELDS = new Set(["bookable_for_users"]);
const FIELD_ALIASES = {
  is_booking_controlled: ["isBookingControlled"],
  bookable_for_users: ["bookableForUsers"],
  cidm_group_name: ["cidmGroupName"],
};

const isValueObject = (value) => {
  return Boolean(value) && typeof value === "object" && Object.prototype.hasOwnProperty.call(value, "value");
};

const toArrayValue = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (isValueObject(value)) {
    if (Array.isArray(value.value)) {
      return value.value;
    }

    return value.value ? [value.value] : [];
  }

  return typeof value === "string" && value.length > 0 ? [value] : [];
};

const toSingleValue = (value, defaultValue) => {
  if (Array.isArray(value)) {
    return value.length > 0 ? value[0] : defaultValue;
  }

  if (isValueObject(value)) {
    return value.value;
  }

  return value;
};

const readAccessControlValue = (value, shouldReturnArray, defaultValue) => {
  if (shouldReturnArray) {
    return toArrayValue(value);
  }

  return toSingleValue(value, defaultValue);
};

const resolveAccessControlValue = (source, key, shouldReturnArray, defaultValue) => {
  if (!source || !Object.prototype.hasOwnProperty.call(source, key)) {
    return undefined;
  }

  const resolved = readAccessControlValue(source[key], shouldReturnArray, defaultValue);
  return resolved ?? defaultValue;
};

const getAccessControlFieldValue = (data, key, defaultValue = undefined) => {
  if (!data || !key) {
    return defaultValue;
  }

  const shouldReturnArray = MULTI_VALUE_FIELDS.has(key);

  const candidateKeys = [key, ...(FIELD_ALIASES[key] || [])];

  for (const candidateKey of candidateKeys) {
    const directValue = resolveAccessControlValue(data, candidateKey, shouldReturnArray, defaultValue);
    if (directValue !== undefined) {
      return directValue;
    }

    const nestedValue = resolveAccessControlValue(data.fields, candidateKey, shouldReturnArray, defaultValue);
    if (nestedValue !== undefined) {
      return nestedValue;
    }
  }

  return defaultValue;
};

const normalizeCidmGroups = (cidmGroups = []) => {
  if (!Array.isArray(cidmGroups)) {
    return [];
  }

  return cidmGroups
    .filter((value) => value !== undefined && value !== null)
    .map((value) => String(value).toLowerCase());
};

const normalizeBookableUsers = (bookableForUsers) => {
  if (Array.isArray(bookableForUsers)) {
    return bookableForUsers;
  }

  if (bookableForUsers === undefined || bookableForUsers === null) {
    return [];
  }

  return [bookableForUsers];
};

const hasCidmGroupAccess = (cidmGroupName, userCidmGroups) => {
  if (!cidmGroupName) {
    return false;
  }

  const normalizedCidmGroupName = cidmGroupName.toLowerCase();
  return userCidmGroups.some((userCidmGroup) => normalizedCidmGroupName === userCidmGroup);
};

const hasDirectBookingAccess = (bookableForUsers, storeUser) => {
  return bookableForUsers.some((user) => user === storeUser?.id || user === storeUser?.user);
};

const getUserAccessState = ({ cidmGroupName, userCidmGroups, bookableForUsers, storeUser }) => {
  if (cidmGroupName) {
    return hasCidmGroupAccess(cidmGroupName, userCidmGroups);
  }

  return hasDirectBookingAccess(bookableForUsers, storeUser);
};

const resolveAccessControlStatus = ({ isBookingControlled, userHasAccess, cidmGroupName }) => {
  if (!isBookingControlled) {
    return accessControlStatus.allowed;
  }

  if (userHasAccess) {
    return accessControlStatus.allowed;
  }

  if (cidmGroupName) {
    return accessControlStatus.trainingRequired;
  }

  return accessControlStatus.restricted;
};

export const getBookingAccessControlState = (data, storeUser = {}) => {
  const cidmGroupName = getAccessControlFieldValue(data, "cidm_group_name");
  const rawControlled = getAccessControlFieldValue(data, "is_booking_controlled");
  const isBookingControlled = rawControlled === "true" || rawControlled === true || !!cidmGroupName;
  const bookableForUsers = normalizeBookableUsers(getAccessControlFieldValue(data, "bookable_for_users"));
  const userCidmGroups = normalizeCidmGroups(storeUser?.cidmGroups);
  const userHasAccess = getUserAccessState({ cidmGroupName, userCidmGroups, bookableForUsers, storeUser });
  const status = resolveAccessControlStatus({ isBookingControlled, userHasAccess, cidmGroupName });
  return { status, isBookingControlled, userHasAccess };
};

export const isAllowedAccessControlStatus = (data, storeUser = {}) => {
  return getBookingAccessControlState(data, storeUser).status === accessControlStatus.allowed;
};
